/**
 * IndoorMapController
 * Manages indoor mapping state across all Wingman map instances.
 * 100% additive — never touches existing spatial contracts.
 *
 * PLATFORM CONTRACTS PRESERVED:
 *   Contract 3 (FlyTo): All navigation routed through SPATIAL_INTENT
 *   Contract 4 (Spatial): Uses standard setConfigProperty API
 *   Contract 6 (Performance): Indoor only activates at zoom ≥ 16
 */
import mapboxgl from 'mapbox-gl';
import { telemetry } from './utils/telemetry';
import { spatialEventBus } from '../../../wingman_platform_hdri/src/spatialSync/spatialEventBus';
import { indoorV3QueryAdapter } from './indoor/IndoorV3QueryAdapter';
// R1.6R3: Bridge certified route engine to Mapbox runtime (side-effect: registers WingmanIndoorRoute)
import { indoorRouteBridge, type BridgeRouteAndVisualizeResult } from './indoor/IndoorRouteBridge';
import { smartCameraMove } from './smartCameraRouter';

// Known airports with indoor coverage in Mapbox Standard tileset
export interface IndoorAirportMetadata {
    center: [number, number];
    name: string;
    iata: string;
    defaultZoom: number;
    defaultPitch: number;
    defaultBearing: number;
}

export const INDOOR_AIRPORTS: Record<string, IndoorAirportMetadata> = {
    'JFK': { center: [-73.7781, 40.6413], name: 'John F. Kennedy International Airport', iata: 'JFK', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 35 },
    'LAX': { center: [-118.4085, 33.9416], name: 'Los Angeles International Airport', iata: 'LAX', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'SFO': { center: [-122.3790, 37.6213], name: 'San Francisco International Airport', iata: 'SFO', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: -30 },
    'LHR': { center: [-0.4543, 51.4700], name: 'London Heathrow Airport', iata: 'LHR', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 90 },
    'CDG': { center: [2.5479, 49.0097], name: 'Paris Charles de Gaulle Airport', iata: 'CDG', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 45 },
    'NRT': { center: [140.3929, 35.7720], name: 'Tokyo Narita International Airport', iata: 'NRT', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'DXB': { center: [55.3644, 25.2532], name: 'Dubai International Airport', iata: 'DXB', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 120 },
    'SIN': { center: [103.9915, 1.3644], name: 'Singapore Changi Airport', iata: 'SIN', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'HND': { center: [139.7798, 35.5494], name: 'Tokyo Haneda Airport', iata: 'HND', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'FRA': { center: [8.5622, 50.0379], name: 'Frankfurt Airport', iata: 'FRA', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'ORD': { center: [-87.9048, 41.9742], name: "Chicago O'Hare International Airport", iata: 'ORD', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'ATL': { center: [-84.4281, 33.6407], name: 'Hartsfield-Jackson Atlanta International Airport', iata: 'ATL', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 90 },
    'MIA': { center: [-80.2870, 25.7959], name: 'Miami International Airport', iata: 'MIA', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: -45 },
    'EWR': { center: [-74.1745, 40.6895], name: 'Newark Liberty International Airport', iata: 'EWR', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
    'SEA': { center: [-122.3088, 47.4502], name: 'Seattle-Tacoma International Airport', iata: 'SEA', defaultZoom: 16.5, defaultPitch: 60, defaultBearing: 0 },
};

export const AIRPORT_ALIASES: Record<string, string> = {
    'kennedy airport': 'JFK',
    'kennedy': 'JFK',
    'john f kennedy': 'JFK',
    'john f kennedy airport': 'JFK',
    'jfk airport': 'JFK',
    'miami airport': 'MIA',
    'mia airport': 'MIA',
    'los angeles airport': 'LAX',
    'lax airport': 'LAX',
    'atlanta airport': 'ATL',
    'hartsfield': 'ATL',
    'hartsfield jackson': 'ATL',
    'hartsfield-jackson': 'ATL',
    'atl airport': 'ATL',
    'ohare': 'ORD',
    "o'hare": 'ORD',
    'ohare airport': 'ORD',
    "o'hare airport": 'ORD',
    'chicago airport': 'ORD',
    'heathrow': 'LHR',
    'heathrow airport': 'LHR',
    'london heathrow': 'LHR',
    'charles de gaulle': 'CDG',
    'cdg airport': 'CDG',
    'paris airport': 'CDG',
    'narita': 'NRT',
    'narita airport': 'NRT',
    'tokyo narita': 'NRT',
    'haneda': 'HND',
    'haneda airport': 'HND',
    'tokyo haneda': 'HND',
    'dubai airport': 'DXB',
    'changi': 'SIN',
    'changi airport': 'SIN',
    'singapore airport': 'SIN',
    'frankfurt airport': 'FRA',
    'san francisco airport': 'SFO',
    'sfo airport': 'SFO',
    'newark airport': 'EWR',
    'newark liberty': 'EWR',
    'seattle airport': 'SEA',
    'seatac': 'SEA',
    'seatac airport': 'SEA',
};

export type IndoorState = 'DISABLED' | 'ENABLED' | 'ACTIVE_VENUE' | 'BROWSING_FLOOR';

export class IndoorMapController {
    private indoorEnabled = false;
    private state: IndoorState = 'DISABLED';
    private activeAirport: string | null = null;
    private indoorControls = new Map<string, any>();
    private stateListeners = new Set<(state: IndoorState, airport: string | null) => void>();
    private primaryMap: mapboxgl.Map | null = null;

    /**
     * Enable indoor mapping on a Mapbox map instance.
     * Call this after map.on('style.load') fires.
     * This is safe to call multiple times — idempotent.
     */
    enableIndoor(map: mapboxgl.Map, instanceId: string): void {
        this.primaryMap = map;
        try {
            (map as any).setConfigProperty('basemap', 'showIndoor', true);

            // Ensure explicit read-only Indoor v3 vector source is registered
            indoorV3QueryAdapter.ensureSource(map);

            // Add floor selector control if not already added for this instance
            if (!this.indoorControls.has(instanceId)) {
                const IndoorControlClass = (mapboxgl as any).IndoorControl;
                if (IndoorControlClass) {
                    const control = new IndoorControlClass();
                    map.addControl(control);
                    this.indoorControls.set(instanceId, control);
                    telemetry.info('INDOOR_MAP', 'CONTROL_ADDED', { instanceId });
                } else {
                    telemetry.warn('INDOOR_MAP', 'INDOOR_CONTROL_NOT_AVAILABLE', {
                        instanceId,
                        hint: 'mapboxgl.IndoorControl may require a newer Mapbox GL JS version'
                    });
                }
            }

            this.indoorEnabled = true;
            this.setState('ENABLED');
            telemetry.info('INDOOR_MAP', 'ENABLED', { instanceId });

            // Listen for zoom changes to detect active venue state
            map.on('zoom', () => {
                const zoom = map.getZoom();
                if (zoom >= 16 && this.indoorEnabled) {
                    if (this.state !== 'ACTIVE_VENUE' && this.state !== 'BROWSING_FLOOR') {
                        const center: [number, number] = [map.getCenter().lng, map.getCenter().lat];
                        const airport = this.detectAirport(center);
                        if (airport) {
                            this.activeAirport = airport.iata;
                            this.setState('ACTIVE_VENUE');
                            telemetry.info('INDOOR_MAP', 'VENUE_ENTERED', {
                                airport: airport.name, iata: airport.iata
                            });
                        }
                    }
                } else if (this.state === 'ACTIVE_VENUE' || this.state === 'BROWSING_FLOOR') {
                    this.activeAirport = null;
                    this.setState('ENABLED');
                    telemetry.info('INDOOR_MAP', 'VENUE_EXITED');
                }
            });
        } catch (e) {
            telemetry.warn('INDOOR_MAP', 'ENABLE_FAILED', { instanceId, error: String(e) });
        }
    }

    /**
     * Disable indoor mapping on a Mapbox map instance.
     */
    disableIndoor(map: mapboxgl.Map, instanceId: string): void {
        try {
            (map as any).setConfigProperty('basemap', 'showIndoor', false);

            const control = this.indoorControls.get(instanceId);
            if (control) {
                map.removeControl(control);
                this.indoorControls.delete(instanceId);
            }

            this.indoorEnabled = false;
            this.activeAirport = null;
            this.setState('DISABLED');
            telemetry.info('INDOOR_MAP', 'DISABLED', { instanceId });
        } catch (e) {
            telemetry.warn('INDOOR_MAP', 'DISABLE_FAILED', { instanceId, error: String(e) });
        }
    }

    /**
     * Check if coordinates are near a known indoor-supported airport.
     * Uses haversine distance with a 3km threshold.
     */
    detectAirport(coordinates: [number, number]): IndoorAirportMetadata | null {
        for (const [code, airport] of Object.entries(INDOOR_AIRPORTS)) {
            const distance = this.haversineDistance(coordinates, airport.center);
            if (distance < 3) { // Within 3km of airport center
                return airport;
            }
        }
        return null;
    }

    /**
     * Resolve an airport from a search query string.
     * Matches IATA codes, natural language utterances, aliases, and partial names.
     */
    resolveAirportFromQuery(query: string): IndoorAirportMetadata | null {
        if (!query || typeof query !== 'string') return null;

        // Clean query by removing common navigational command prefixes and punctuation
        let clean = query.trim().toLowerCase();
        clean = clean.replace(/^(?:fly\s+to|take\s+me\s+to|navigate\s+to|go\s+to|show\s+me|head\s+to|fly\s+into|bring\s+me\s+to)\s+/i, '');
        clean = clean.replace(/[?.,!]/g, '').trim();

        const upper = clean.toUpperCase();

        // 1. Direct IATA code match on cleaned query
        if (INDOOR_AIRPORTS[upper]) {
            return INDOOR_AIRPORTS[upper];
        }

        // 2. Direct alias lookup
        if (AIRPORT_ALIASES[clean]) {
            const iata = AIRPORT_ALIASES[clean];
            if (INDOOR_AIRPORTS[iata]) {
                return INDOOR_AIRPORTS[iata];
            }
        }

        // 3. Token-based IATA word search (e.g. "fly to jfk airport" -> tokens ["jfk", "airport"])
        const tokens = clean.split(/\s+/);
        for (const token of tokens) {
            const tokenUpper = token.toUpperCase();
            if (INDOOR_AIRPORTS[tokenUpper]) {
                return INDOOR_AIRPORTS[tokenUpper];
            }
        }

        // 4. Substring / Alias matching
        for (const [alias, iata] of Object.entries(AIRPORT_ALIASES)) {
            if (clean.includes(alias) && INDOOR_AIRPORTS[iata]) {
                return INDOOR_AIRPORTS[iata];
            }
        }

        // 5. Canonical airport name match (prevents bare city names like "paris" from falsely matching)
        for (const airport of Object.values(INDOOR_AIRPORTS)) {
            const nameLower = airport.name.toLowerCase();
            if (clean.includes(nameLower) || nameLower === clean || (clean.includes('airport') && nameLower.includes(clean))) {
                return airport;
            }
        }

        return null;
    }

    /**
     * Fly to an airport with indoor-optimized camera settings.
     * Routes through the standard SPATIAL_INTENT pipeline — Contract 3 preserved.
     */
    flyToAirportIndoor(airportCode: string): boolean {
        const airport = INDOOR_AIRPORTS[airportCode.toUpperCase()];
        if (!airport) {
            telemetry.warn('INDOOR_MAP', 'UNKNOWN_AIRPORT', { code: airportCode });
            return false;
        }

        this.activeAirport = airportCode.toUpperCase();

        // Route through existing FlyTo pipeline — Contract 3 (FlyTo) preserved
        spatialEventBus.emit('SPATIAL_INTENT', {
            intentType: 'NAVIGATE',
            source: 'INDOOR_MAP',
            payload: {
                center: airport.center,
                zoom: airport.defaultZoom,
                pitch: airport.defaultPitch,
                bearing: airport.defaultBearing,
                duration: 5000,
            }
        });

        telemetry.info('INDOOR_MAP', 'FLY_TO_AIRPORT', {
            code: airportCode,
            name: airport.name,
            zoom: airport.defaultZoom
        });

        return true;
    }

    /**
     * Get all known airports with indoor support.
     */
    getKnownAirports() {
        return { ...INDOOR_AIRPORTS };
    }

    /**
     * Get the currently active airport (if viewing one at zoom ≥ 16).
     */
    getActiveAirport(): string | null {
        return this.activeAirport;
    }

    /**
     * Get the active airport's full metadata.
     */
    getActiveAirportMetadata(): typeof INDOOR_AIRPORTS[string] | null {
        if (!this.activeAirport) return null;
        return INDOOR_AIRPORTS[this.activeAirport] || null;
    }

    /**
     * Check if indoor mapping is currently enabled.
     */
    isEnabled(): boolean {
        return this.indoorEnabled;
    }

    /**
     * Get current indoor state machine state.
     */
    getState(): IndoorState {
        return this.state;
    }

    /**
     * Subscribe to indoor state changes.
     */
    onStateChange(listener: (state: IndoorState, airport: string | null) => void): () => void {
        this.stateListeners.add(listener);
        return () => this.stateListeners.delete(listener);
    }

    /**
     * Remove a specific indoor control by instance ID.
     * Call this when a map instance is being unmounted.
     */
    removeControl(map: mapboxgl.Map, instanceId: string): void {
        const control = this.indoorControls.get(instanceId);
        if (control) {
            try {
                map.removeControl(control);
            } catch (e) {
                // Control may already be removed if map is destroyed
            }
            this.indoorControls.delete(instanceId);
        }
    }

    /**
     * Get the authoritative visible Mapbox instance.
     */
    getMap(): mapboxgl.Map | null {
        return this.primaryMap || (typeof window !== 'undefined' ? (window as any).map : null);
    }

    /**
     * Solve and render an indoor route using the existing IndoorRouteBridge authority.
     * Writes to the visible Mapbox instance.
     */
    calculateAndRenderRoute(
        originQuery: string,
        destinationQuery: string,
        activeFloor?: number
    ): BridgeRouteAndVisualizeResult {
        const map = this.getMap();
        if (!map) {
            telemetry.warn('INDOOR_MAP', 'ROUTE_NO_MAP_INSTANCE', { origin: originQuery, destination: destinationQuery });
            return {
                status: 'MAP_NOT_READY',
                route: null,
                dataset: null,
                graph: null,
                fixtureUsed: false,
                visualization: null,
                error: 'No active Mapbox instance available'
            };
        }
        const result = indoorRouteBridge.routeAndVisualize(map, originQuery, destinationQuery, activeFloor);
        if (result.status === 'ROUTE_FOUND' && result.visualization?.bounds) {
            const airportMeta = this.getActiveAirportMetadata();
            smartCameraMove(map, {
                bounds: result.visualization.bounds,
                padding: { top: 80, bottom: 80, left: 80, right: 440 },
                maxZoom: 19,
                pitch: airportMeta?.defaultPitch ?? 50,
                bearing: airportMeta?.defaultBearing ?? 35,
                duration: 2000,
            });
        }
        return result;
    }

    /**
     * Full cleanup — call when the entire application is shutting down.
     */
    cleanup(): void {
        this.indoorControls.clear();
        this.stateListeners.clear();
        this.activeAirport = null;
        this.indoorEnabled = false;
        this.state = 'DISABLED';
        this.primaryMap = null;
    }

    private setState(newState: IndoorState): void {
        if (this.state === newState) return;
        const oldState = this.state;
        this.state = newState;
        telemetry.forensic('INDOOR_MAP', 'STATE_TRANSITION', {
            from: oldState, to: newState, airport: this.activeAirport
        });
        this.stateListeners.forEach(listener => {
            try {
                listener(newState, this.activeAirport);
            } catch (e) {
                telemetry.warn('INDOOR_MAP', 'LISTENER_ERROR', { error: String(e) });
            }
        });
    }

    /**
     * Haversine distance in km between two [lng, lat] coordinates.
     */
    private haversineDistance(a: [number, number], b: [number, number]): number {
        const R = 6371; // Earth radius in km
        const dLat = (b[1] - a[1]) * Math.PI / 180;
        const dLon = (b[0] - a[0]) * Math.PI / 180;
        const lat1 = a[1] * Math.PI / 180;
        const lat2 = b[1] * Math.PI / 180;
        const h = Math.sin(dLat / 2) ** 2 +
                  Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    }
}

// Singleton export — matches Wingman's existing service pattern
export const indoorMapController = new IndoorMapController();

// Global access for voice commands and console debugging
if (typeof window !== 'undefined') {
    (window as any).WingmanIndoor = {
        flyTo: (code: string) => indoorMapController.flyToAirportIndoor(code),
        airports: () => indoorMapController.getKnownAirports(),
        state: () => indoorMapController.getState(),
        active: () => indoorMapController.getActiveAirportMetadata(),
        getMap: () => indoorMapController.getMap(),
        route: (origin: string, destination: string, activeFloor?: number) =>
            indoorMapController.calculateAndRenderRoute(origin, destination, activeFloor),
    };
}
